/**
 * Un file .xlsx vero, scritto a mano.
 *
 * Prima l'esportazione produceva SpreadsheetML - un XML di vent'anni fa - con
 * estensione .xls. Excel lo apriva, ma prima mostrava "Il formato e
 * l'estensione di 'analisi-2026.xls' non corrispondono. Il file potrebbe
 * essere danneggiato o non sicuro": un avviso di sicurezza davanti a un
 * report aziendale, che chi lo riceve non sa se ignorare.
 *
 * Un .xlsx e' uno zip di file XML. Le due parti che servivano - lo zip e il
 * foglio - stanno qui, in un centinaio di righe: una libreria di generazione
 * xlsx nel pacchetto del browser peserebbe centinaia di kilobyte per fare una
 * tabella senza formule ne' grafici.
 *
 * I file dentro lo zip sono archiviati **senza compressione**: serve solo il
 * CRC32, non un compressore, e un report di duecento righe pesa comunque
 * poche decine di kilobyte.
 */

const TESTO = new TextEncoder();

/** Tabella del CRC32, calcolata una volta sola alla prima esportazione. */
let tabellaCrc: Uint32Array | null = null;

function crc32(dati: Uint8Array): number {
  if (tabellaCrc === null) {
    tabellaCrc = new Uint32Array(256);
    for (let i = 0; i < 256; i++) {
      let c = i;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      tabellaCrc[i] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (const byte of dati) {
    crc = (tabellaCrc[(crc ^ byte) & 0xff] as number) ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

interface VoceZip {
  nome: string;
  dati: Uint8Array;
}

/**
 * Lo zip, in formato "stored": nessuna compressione, nessun descrittore.
 *
 * Data e ora sono fisse. In un archivio generato al volo non dicono niente di
 * utile - il file lo si e' appena scaricato - e una data variabile rende due
 * esportazioni identiche diverse byte per byte, che e' scomodo da provare.
 */
export function creaZip(voci: readonly VoceZip[]): Uint8Array<ArrayBuffer> {
  const ORA = 0; // 00:00
  const DATA = ((2026 - 1980) << 9) | (1 << 5) | 1; // 1 gennaio 2026

  const pezzi: Uint8Array[] = [];
  const centrale: Uint8Array[] = [];
  let posizione = 0;

  for (const voce of voci) {
    const nome = TESTO.encode(voce.nome);
    const crc = crc32(voce.dati);

    const locale = new Uint8Array(30 + nome.length);
    const v = new DataView(locale.buffer);
    v.setUint32(0, 0x04034b50, true);
    v.setUint16(4, 20, true); // versione minima
    v.setUint16(6, 0x0800, true); // nomi in UTF-8
    v.setUint16(8, 0, true); // nessuna compressione
    v.setUint16(10, ORA, true);
    v.setUint16(12, DATA, true);
    v.setUint32(14, crc, true);
    v.setUint32(18, voce.dati.length, true);
    v.setUint32(22, voce.dati.length, true);
    v.setUint16(26, nome.length, true);
    locale.set(nome, 30);

    const testata = new Uint8Array(46 + nome.length);
    const c = new DataView(testata.buffer);
    c.setUint32(0, 0x02014b50, true);
    c.setUint16(4, 20, true);
    c.setUint16(6, 20, true);
    c.setUint16(8, 0x0800, true);
    c.setUint16(10, 0, true);
    c.setUint16(12, ORA, true);
    c.setUint16(14, DATA, true);
    c.setUint32(16, crc, true);
    c.setUint32(20, voce.dati.length, true);
    c.setUint32(24, voce.dati.length, true);
    c.setUint16(28, nome.length, true);
    c.setUint32(42, posizione, true);
    testata.set(nome, 46);

    pezzi.push(locale, voce.dati);
    centrale.push(testata);
    posizione += locale.length + voce.dati.length;
  }

  const dimensioneCentrale = centrale.reduce((s, x) => s + x.length, 0);
  const coda = new Uint8Array(22);
  const f = new DataView(coda.buffer);
  f.setUint32(0, 0x06054b50, true);
  f.setUint16(8, voci.length, true);
  f.setUint16(10, voci.length, true);
  f.setUint32(12, dimensioneCentrale, true);
  f.setUint32(16, posizione, true);

  const tutto = [...pezzi, ...centrale, coda];
  const totale = tutto.reduce((s, x) => s + x.length, 0);
  const archivio = new Uint8Array(new ArrayBuffer(totale));
  let scritto = 0;
  for (const p of tutto) {
    archivio.set(p, scritto);
    scritto += p.length;
  }
  return archivio;
}

function xml(testo: string): string {
  let pulito = '';
  for (const carattere of testo) {
    const codice = carattere.codePointAt(0) ?? 0;
    // I caratteri di controllo non sono ammessi in XML, e Excel rifiuterebbe
    // il file intero per un solo byte finito dentro una nota. Tabulazione,
    // ritorno a capo e fine riga sono gli unici ammessi.
    if (codice < 32 && codice !== 9 && codice !== 10 && codice !== 13) continue;
    if (carattere === '&') pulito += '&amp;';
    else if (carattere === '<') pulito += '&lt;';
    else if (carattere === '>') pulito += '&gt;';
    else if (carattere === '"') pulito += '&quot;';
    else pulito += carattere;
  }
  return pulito;
}

/** A, B, ... Z, AA, AB: il nome della colonna, contando da zero. */
export function nomeColonna(indice: number): string {
  let nome = '';
  let n = indice;
  do {
    nome = String.fromCharCode(65 + (n % 26)) + nome;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return nome;
}

function cella(riferimento: string, valore: string, numerica: boolean, stile: number): string {
  const s = stile > 0 ? ` s="${stile}"` : '';
  if (numerica) return `<c r="${riferimento}"${s}><v>${valore}</v></c>`;
  return `<c r="${riferimento}"${s} t="inlineStr"><is><t xml:space="preserve">${xml(valore)}</t></is></c>`;
}

export interface FoglioDaScrivere {
  nome: string;
  intestazioni: readonly string[];
  /** Le righe, gia' come testo. I decimali con la virgola: qui si riconvertono. */
  righe: readonly (readonly string[])[];
  /** Le prime N colonne sono testo; dalla successiva in poi sono numeri. */
  colonneTesto: number;
}

/**
 * Il foglio, come lo scriverebbe Excel.
 *
 * I numeri restano numeri - senno' le somme nel foglio non funzionano, ed e'
 * la prima cosa che ci fa chi apre il file. Le stringhe sono "inline": senza
 * tabella condivisa il file e' un pelo piu' grande e molto piu' semplice.
 */
function foglio(dati: FoglioDaScrivere): string {
  const righe: string[] = [];

  righe.push(
    `<row r="1">${dati.intestazioni
      .map((t, i) => cella(`${nomeColonna(i)}1`, t, false, 1))
      .join('')}</row>`,
  );

  dati.righe.forEach((riga, indice) => {
    const numero = indice + 2;
    const celle = riga
      .map((valore, i) => {
        const numerica = i >= dati.colonneTesto;
        if (!numerica) return cella(`${nomeColonna(i)}${numero}`, valore, false, 0);
        const n = valore.replace(',', '.');
        // Un valore che non e' un numero (una cella vuota) va scritto come
        // testo: <v>ciao</v> aprirebbe il file con un errore di ripristino.
        if (n === '' || Number.isNaN(Number(n))) {
          return cella(`${nomeColonna(i)}${numero}`, valore, false, 0);
        }
        return cella(`${nomeColonna(i)}${numero}`, n, true, 0);
      })
      .join('');
    righe.push(`<row r="${numero}">${celle}</row>`);
  });

  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    // Il blocco titoli: scorrendo duecento righe si perderebbe l'intestazione.
    '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>' +
    `<sheetData>${righe.join('')}</sheetData>` +
    '</worksheet>'
  );
}

const CONTENUTI =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
  '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
  '<Default Extension="xml" ContentType="application/xml"/>' +
  '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
  '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
  '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
  '</Types>';

const RELAZIONI =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
  '</Relationships>';

const RELAZIONI_CARTELLA =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
  '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
  '</Relationships>';

/** Due stili: quello normale e l'intestazione bianca su blu, come nel PDF. */
const STILI =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<fonts count="2">' +
  '<font><sz val="11"/><name val="Calibri"/></font>' +
  '<font><b/><color rgb="FFFFFFFF"/><sz val="11"/><name val="Calibri"/></font>' +
  '</fonts>' +
  '<fills count="3">' +
  '<fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill>' +
  '<fill><patternFill patternType="solid"><fgColor rgb="FF2E5090"/><bgColor indexed="64"/></patternFill></fill>' +
  '</fills>' +
  '<borders count="1"><border/></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="2">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/>' +
  '</cellXfs>' +
  '</styleSheet>';

/** Il pacchetto .xlsx completo, pronto da scaricare. */
export function creaXlsx(dati: FoglioDaScrivere): Uint8Array<ArrayBuffer> {
  const cartella =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ' +
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `<sheets><sheet name="${xml(dati.nome)}" sheetId="1" r:id="rId1"/></sheets>` +
    '</workbook>';

  return creaZip([
    { nome: '[Content_Types].xml', dati: TESTO.encode(CONTENUTI) },
    { nome: '_rels/.rels', dati: TESTO.encode(RELAZIONI) },
    { nome: 'xl/workbook.xml', dati: TESTO.encode(cartella) },
    { nome: 'xl/_rels/workbook.xml.rels', dati: TESTO.encode(RELAZIONI_CARTELLA) },
    { nome: 'xl/styles.xml', dati: TESTO.encode(STILI) },
    { nome: 'xl/worksheets/sheet1.xml', dati: TESTO.encode(foglio(dati)) },
  ]);
}
